import { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Flame,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import { useErrors } from '@/hooks/useErrors';
import { useRealtimeErrors } from '@/hooks/useRealtimeErrors';
import { ErrorDetailsModal } from './ErrorDetailsModal';
import type { ErrorStatus, ErrorSeverity, AppError } from '@/types/error.types';
import { formatRelativeTime } from '@/utils/formatters';

const SEVERITY_PILL = 'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold';

const getSeverityBadge = (severity: string) => {
  switch (severity) {
    case 'critical':
      return (
        <span className={`${SEVERITY_PILL} bg-error/10 text-error`}>
          <Flame size={14} />
          Crítico
        </span>
      );
    case 'high':
      return (
        <span className={`${SEVERITY_PILL} bg-warning/10 text-warning`}>
          <AlertCircle size={14} />
          Alto
        </span>
      );
    case 'medium':
      return (
        <span className={`${SEVERITY_PILL} bg-amber-500/10 text-amber-600`}>
          <AlertTriangle size={14} />
          Médio
        </span>
      );
    default:
      return null;
  }
};

const getStatusBadge = (status: string) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      status === 'resolved' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
    }`}
  >
    {status === 'resolved' ? 'Resolvido' : 'Não resolvido'}
  </span>
);

export function ErrorsAndCrashes() {
  useRealtimeErrors();

  const [statusFilter, setStatusFilter] = useState<'all' | ErrorStatus>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | ErrorSeverity>('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSeverityDropdown, setShowSeverityDropdown] = useState(false);
  const [selectedError, setSelectedError] = useState<AppError | null>(null);
  const [copiedErrorId, setCopiedErrorId] = useState<string | null>(null);

  const { data, isLoading, error } = useErrors({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    severity: severityFilter !== 'all' ? severityFilter : undefined,
  });

  const errors = (data?.errors || []).sort((a, b) => {
    if (a.status === 'unresolved' && b.status === 'resolved') return -1;
    if (a.status === 'resolved' && b.status === 'unresolved') return 1;
    return new Date(b.lastOccurrence).getTime() - new Date(a.lastOccurrence).getTime();
  });

  const unresolvedCount = errors.filter((e) => e.status === 'unresolved').length;
  const totalOccurrences = errors.reduce((sum, e) => sum + (e.occurrences || 0), 0);
  const crashRate =
    errors.length > 0
      ? ((errors.filter((e) => e.type === 'crash').length / errors.length) * 100).toFixed(2)
      : 0;

  const getSeverityLabel = (severity: string) => {
    const labels: Record<string, string> = {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    };
    return labels[severity] || severity;
  };

  const handleCopyError = async (error: AppError) => {
    const errorText = `
=== ERRO - ${error.message} ===

ID: ${error.id}
Severidade: ${getSeverityLabel(error.severity)}
Status: ${error.status === 'resolved' ? 'Resolvido' : 'Não Resolvido'}
Tipo: ${error.type || 'N/A'}

Primeira Ocorrência: ${error.firstOccurrence}
Última Ocorrência: ${error.lastOccurrence}
Total de Ocorrências: ${error.occurrences || 0}
Dispositivos Afetados: ${error.affectedDevices || 0}

${error.affectedVersions && error.affectedVersions.length > 0 ? `Versões Afetadas: ${error.affectedVersions.join(', ')}\n` : ''}
${error.stackTrace ? `\nStack Trace:\n${error.stackTrace}` : ''}
${error.notes ? `\nNotas de Resolução:\n${error.notes}` : ''}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      setCopiedErrorId(error.id);
      setTimeout(() => setCopiedErrorId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Não foi possível copiar para o clipboard');
    }
  };

  if (error) {
    return (
      <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Erros & Crashes' }]}>
        <div className="p-3 sm:p-4 lg:p-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <h2 className="mb-2 text-xl font-semibold text-text-title">
                Erro ao carregar erros
              </h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar a lista de erros. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Erros & Crashes' }]}>
      <div className="p-3 sm:p-4 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-3xl font-semibold text-text-title">Erros e Crashes</h2>
            <p className="text-base text-text-secondary">
              Monitore, agrupe e resolva os erros que afetam seus usuários.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStatusDropdown(!showStatusDropdown);
                  setShowSeverityDropdown(false);
                }}
                className="flex h-10 items-center gap-2 rounded-lg border border-border-strong bg-card px-4 text-sm font-medium text-text-title hover:bg-muted/50"
              >
                <span>
                  Status:{' '}
                  {statusFilter === 'all'
                    ? 'Todos'
                    : statusFilter === 'resolved'
                      ? 'Resolvido'
                      : 'Não Resolvido'}
                </span>
                <ChevronDown size={18} />
              </button>
              {showStatusDropdown && (
                <div className="absolute right-0 z-10 mt-2 w-48 rounded-lg border border-border bg-card shadow-lg">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setShowStatusDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('unresolved');
                      setShowStatusDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    Não Resolvido
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('resolved');
                      setShowStatusDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    Resolvido
                  </button>
                </div>
              )}
            </div>

            {/* Severity Filter */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSeverityDropdown(!showSeverityDropdown);
                  setShowStatusDropdown(false);
                }}
                className="flex h-10 items-center gap-2 rounded-lg border border-border-strong bg-card px-4 text-sm font-medium text-text-title hover:bg-muted/50"
              >
                <span>
                  Severidade:{' '}
                  {severityFilter === 'all'
                    ? 'Todas'
                    : severityFilter === 'critical'
                      ? 'Critical'
                      : severityFilter === 'high'
                        ? 'High'
                        : 'Medium'}
                </span>
                <ChevronDown size={18} />
              </button>
              {showSeverityDropdown && (
                <div className="absolute right-0 z-10 mt-2 w-48 rounded-lg border border-border bg-card shadow-lg">
                  <button
                    onClick={() => {
                      setSeverityFilter('all');
                      setShowSeverityDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    Todas
                  </button>
                  <button
                    onClick={() => {
                      setSeverityFilter('critical');
                      setShowSeverityDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    Critical
                  </button>
                  <button
                    onClick={() => {
                      setSeverityFilter('high');
                      setShowSeverityDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    High
                  </button>
                  <button
                    onClick={() => {
                      setSeverityFilter('medium');
                      setShowSeverityDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    Medium
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 lg:mt-6 xl:grid-cols-4 lg:gap-6">
          <div className="rounded-xl border border-border bg-card p-4 shadow-card transition-shadow hover:shadow-card-hover lg:p-6">
            <p className="text-xs font-medium text-text-secondary sm:text-sm">Erros únicos</p>
            <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-text-title lg:text-4xl">
              {isLoading ? '—' : errors.length}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 shadow-card transition-shadow hover:shadow-card-hover lg:p-6">
            <p className="text-xs font-medium text-text-secondary sm:text-sm">Total de ocorrências</p>
            <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-text-title lg:text-4xl">
              {isLoading ? '—' : totalOccurrences.toLocaleString('pt-BR')}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 shadow-card transition-shadow hover:shadow-card-hover lg:p-6">
            <p className="text-xs font-medium text-text-secondary sm:text-sm">Não resolvidos</p>
            <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-error lg:text-4xl">
              {isLoading ? '—' : unresolvedCount}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 shadow-card transition-shadow hover:shadow-card-hover lg:p-6">
            <p className="text-xs font-medium text-text-secondary sm:text-sm">Taxa de crash</p>
            <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-text-title lg:text-4xl">
              {isLoading ? '—' : `${crashRate}%`}
            </p>
          </div>
        </div>

        {/* Error List */}
        <div className="mt-8 rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border p-4">
            <h3 className="text-lg font-semibold text-text-title">Todos os Erros</h3>
          </div>

          {/* Table */}
          <div className="flex flex-col">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="size-8 animate-spin text-primary" />
              </div>
            ) : errors.length > 0 ? (
              <>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-6 border-b border-border px-6 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  <div className="col-span-5">Erro</div>
                  <div className="col-span-2 text-center">Ocorrências</div>
                  <div className="col-span-2 text-center">Dispositivos</div>
                  <div className="col-span-3 text-right">Última Ocorrência</div>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-border">
                  {errors.map((error, index) => {
                    const isResolved = error.status === 'resolved';
                    const prevError = index > 0 ? errors[index - 1] : null;
                    const showResolvedDivider = isResolved && prevError?.status === 'unresolved';

                    return (
                      <>
                        {showResolvedDivider && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-px flex-1 bg-green-200"></div>
                              <span className="flex items-center gap-2 text-sm font-semibold text-green-700">
                                <Check size={16} />
                                Erros Resolvidos
                              </span>
                              <div className="h-px flex-1 bg-green-200"></div>
                            </div>
                          </div>
                        )}
                        <div
                          key={error.id}
                          className={`grid grid-cols-12 items-center gap-6 px-6 py-4 transition-colors ${
                            isResolved
                              ? 'bg-green-50/30 opacity-60 hover:opacity-80 hover:bg-green-50/50'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="col-span-5 flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              {getSeverityBadge(error.severity)}
                              {getStatusBadge(error.status)}
                              {(error.source === 'API' || error.metadata?.source === 'API') && (
                                <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs font-bold text-white">
                                  API
                                </span>
                              )}
                            </div>
                            <p className="line-clamp-2 text-sm font-medium text-text-title">
                              {error.message}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-text-secondary">
                              {error.stackTrace && (
                                <span className="rounded bg-muted px-1.5 py-0.5 font-mono truncate max-w-[200px]">
                                  {error.stackTrace.split('\n')[0]}
                                </span>
                              )}
                              <span>em</span>
                              {error.affectedVersions?.map((version) => (
                                <span
                                  key={version}
                                  className="rounded bg-muted px-1.5 py-0.5 font-mono"
                                >
                                  {version}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="col-span-2 text-center text-sm tabular-nums text-text-title">
                            {error.occurrences?.toLocaleString('pt-BR') || '0'}
                          </div>
                          <div className="col-span-2 flex items-center justify-center gap-3 text-sm text-text-title">
                            <span className="min-w-[2ch] tabular-nums">{error.affectedDevices || '0'}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyError(error);
                              }}
                              aria-label="Copiar erro"
                              title="Copiar erro"
                              className={`flex size-7 shrink-0 items-center justify-center rounded transition-colors active:scale-95 ${
                                copiedErrorId === error.id
                                  ? 'bg-success/15 text-success'
                                  : 'text-text-secondary hover:bg-muted hover:text-text-title'
                              }`}
                            >
                              {copiedErrorId === error.id ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                          </div>
                          <div className="col-span-3 flex items-center justify-end gap-6">
                            <span className="shrink-0 whitespace-nowrap text-sm text-text-secondary">
                              {error.lastOccurrence
                                ? formatRelativeTime(error.lastOccurrence)
                                : '—'}
                            </span>
                            <button
                              onClick={() => setSelectedError(error)}
                              className="h-9 shrink-0 whitespace-nowrap rounded-lg bg-primary px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98]"
                            >
                              Ver detalhes
                            </button>
                          </div>
                        </div>
                      </>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="mb-2 text-lg font-medium text-text-title">
                    Nenhum erro encontrado
                  </p>
                  <p className="text-sm text-text-secondary">
                    Não há erros registrados com os filtros selecionados
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && errors.length > 0 && (
            <div className="flex items-center justify-between border-t border-border p-4 text-sm text-text-secondary">
              <span>
                Mostrando 1-{errors.length} de {data?.total ?? errors.length} erros
              </span>
              <div className="flex items-center gap-2">
                <button className="flex h-8 w-8 items-center justify-center rounded-lg border border-border-strong hover:bg-muted">
                  <ChevronLeft size={18} />
                </button>
                <button className="flex h-8 w-8 items-center justify-center rounded-lg border border-border-strong hover:bg-muted">
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Details Modal */}
      {selectedError && (
        <ErrorDetailsModal
          isOpen={!!selectedError}
          onClose={() => setSelectedError(null)}
          error={selectedError}
        />
      )}
    </Layout>
  );
}
